/**
 * Reading a picked file the way this API takes files.
 *
 * Several endpoints want the CONTENT inline as base64 rather than a multipart part —
 * `SaveLogo`, `SaveCourse`'s banner, `AddAttachment` — and two of them also want the
 * extension in a field of its own. That is enough repetition to be worth one place.
 *
 * `src/features/settings/settingsService.js` still carries an older private copy; it should
 * move here rather than a third one appearing.
 */

/**
 * The file's content as BARE base64 — no `data:…;base64,` prefix.
 *
 * The prefix is dropped rather than sent: some handlers strip it and some do not, and the
 * ones that do not write it into the file.
 *
 * @param {File|null} file
 * @returns {Promise<string>} empty for no file
 */
export function fileToBase64(file) {
  if (!file) return Promise.resolve('')

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('Could not read the selected file'))
    reader.onload = () => resolve(String(reader.result).split(',').pop() ?? '')
    reader.readAsDataURL(file)
  })
}

/**
 * The extension, lower-cased and without the dot — `AddAttachment` builds its stored name
 * as `{guid}.{type}`, so this is what goes in the `…Type` field. NOT the MIME type, despite
 * the field's name.
 */
export const extensionOf = (file) =>
  file?.name?.includes('.') ? file.name.split('.').pop().toLowerCase() : ''

/**
 * The extension of a file NAME, lower-cased and without the dot.
 *
 * `extensionOf` above takes a picked `File`; this takes a name a list already holds. A
 * security report's attachments arrive as `{ name, url }` with NO MIME type, so the name
 * is the only thing that says what the file is.
 */
export function extensionOfName(name) {
  const text = String(name ?? '')
  const dot = text.lastIndexOf('.')
  // `> 0`, not `>= 0`: a dotfile is not an extension.
  return dot > 0 ? text.slice(dot + 1).toLowerCase() : ''
}

/* Extension is all there is to go on, so the map is explicit rather than clever. Anything
   unlisted is `file`, which is a real answer and not a failure. */
const KIND_EXTENSIONS = {
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'avif', 'heic'],
  pdf: ['pdf'],
  email: ['eml', 'msg', 'oft'],
  word: ['doc', 'docx', 'rtf', 'odt'],
  excel: ['xls', 'xlsx', 'csv', 'ods'],
  presentation: ['ppt', 'pptx', 'odp'],
  archive: ['zip', 'rar', '7z', 'tar', 'gz'],
  video: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'],
  audio: ['mp3', 'wav', 'ogg', 'm4a'],
  text: ['txt', 'log', 'json', 'xml', 'md'],
}

const KIND_BY_EXTENSION = Object.fromEntries(
  Object.entries(KIND_EXTENSIONS).flatMap(([kind, list]) => list.map((ext) => [ext, kind])),
)

/**
 * WHAT KIND of thing a file is, for choosing an icon and deciding whether it can be shown
 * as a picture: `image`, `pdf`, `email`, `word`, `excel`, `presentation`, `archive`,
 * `video`, `audio`, `text`, or `file` for everything else.
 */
export const fileKind = (name) => KIND_BY_EXTENSION[extensionOfName(name)] ?? 'file'
